import React, { useState, useEffect } from "react";
import api, { extractApiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import PhoneInput from "@/components/PhoneInput";
import { Sparkles } from "lucide-react";
import { calendarComponents, calendarRowClass } from "@/pages/admin/utils/format";



// -----------------------------------------------------------------------------
// Manual Booking Entry (admin creates a confirmed booking on behalf of a client)
// -----------------------------------------------------------------------------
const ManualBookingForm = ({ onCreated }) => {
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState("");
  const [first, setFirst] = useState("");
  const [middle, setMiddle] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [date, setDate] = useState(undefined);
  const [hour, setHour] = useState(10);
  const [paymentStatus, setPaymentStatus] = useState("paid");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null); // { booking_id, user_id, name, amount }
  const [inviteLoading, setInviteLoading] = useState(false);

  useEffect(() => {
    api.get("/services")
      .then((r) => {
        const consults = (r.data || []).filter((x) => x && x.type === "consultation");
        setServices(consults);
        if (consults.length && !serviceId) setServiceId(consults[0].id);
      })
      .catch(() => toast.error("تعذّر تحميل الخدمات"));
  }, []);  // eslint-disable-line

  const selected = services.find((s) => s.id === serviceId);
  const isImmediate = selected?.is_immediate;
  // Iteration 56 — manual booking is an admin override: show ALL 24 hours
  // (00:00..23:00) regardless of the service's normal customer-facing
  // working window. The backend now also accepts any hour for this endpoint.
  const serviceHours = Array.from({ length: 24 }, (_, i) => i);

  // Auto-select the first valid hour whenever the chosen service changes
  // so the admin never submits an out-of-range hour from a previous service.
  useEffect(() => {
    if (!serviceHours.includes(hour) && serviceHours.length > 0) {
      setHour(serviceHours[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, serviceHours.join(",")]);

  const submit = async () => {
    if (!serviceId) return toast.error("اختر الخدمة");
    if (!first.trim() || !middle.trim() || !last.trim())
      return toast.error("يرجى إدخال الاسم الثلاثي");
    if (!email.trim()) return toast.error("البريد مطلوب");
    if (!phone.trim()) return toast.error("رقم Botim مطلوب");
    if (!date) return toast.error("اختر التاريخ");
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    const slot_iso = `${y}-${m}-${d}T${String(hour).padStart(2, "0")}:00:00`;
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/bookings/manual", {
        first_name: first.trim(),
        middle_name: middle.trim(),
        last_name: last.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim(),
        service_id: serviceId,
        slot_iso,
        payment_status: paymentStatus,
        note: note.trim() || null,
      });
      const baseMsg = paymentStatus === "complimentary"
        ? `تم إنشاء حجز مجاني (Booking #${data.booking_id.slice(0, 8)})`
        : `تم إنشاء الحجز كمدفوع (${Number(data.amount).toLocaleString("ar-SA")} ر.س)`;
      toast.success(baseMsg, {
        description: data.invite_emailed
          ? "📨 تم إرسال رابط الدخول للعميل على بريده مباشرة"
          : undefined,
      });
      // Capture last result so admin can copy a one-time invite link
      setLastResult({
        booking_id: data.booking_id,
        user_id: data.user_id,
        name: `${first.trim()} ${middle.trim()} ${last.trim()}`.trim(),
        amount: data.amount,
        payment_status: paymentStatus,
        email: email.trim().toLowerCase(),
        // Iteration 33 — server returns a generated_password only for *new*
        // user accounts; existing customers keep their old credentials.
        generated_password: data.generated_password || null,
      });
      // Reset form fields (keep lastResult visible)
      setFirst(""); setMiddle(""); setLast(""); setEmail(""); setPhone("");
      setDate(undefined); setHour(10); setNote(""); setPaymentStatus("paid");
      onCreated && onCreated();
    } catch (e) {
      toast.error(extractApiErrorMessage(e, "تعذّر إنشاء الحجز"));
    } finally {
      setSubmitting(false);
    }
  };

  // Calendar disable: weekends only for non-immediate; past for all.
  const manualDisabled = (d) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (d < today) return true;
    if (!isImmediate) {
      const dow = d.getDay();
      if (dow === 5 || dow === 6) return true;
    }
    return false;
  };

  const copyInviteLink = async () => {
    if (!lastResult?.user_id) return;
    setInviteLoading(true);
    try {
      const { data } = await api.post(`/admin/clients/${lastResult.user_id}/invite-link`);
      // Build the URL from the current window origin so the link uses the same
      // public domain the admin is browsing on (avoids leaking the internal
      // ingress hostname when the backend echoes its own request URL).
      const url = `${window.location.origin}/invite/${data.token}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("تم نسخ رابط الدعوة إلى الحافظة");
      } catch {
        // Fallback: show the URL in the toast for manual copy
        toast.success("رابط الدعوة جاهز", { description: url, duration: 12000 });
      }
    } catch (e) {
      toast.error(extractApiErrorMessage(e, "تعذّر إنشاء الرابط"));
    } finally {
      setInviteLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {lastResult && (
        <div
          data-testid="manual-last-result-card"
          className="card-warm p-5 border-2 border-emerald-200 bg-emerald-50/40"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-2 text-emerald-700 text-sm font-medium">
                <Sparkles className="w-4 h-4" /> آخر حجز يدوي
              </div>
              <div className="mt-1 text-[#222222]">
                <strong>{lastResult.name || "العميل"}</strong> · {" "}
                {lastResult.payment_status === "complimentary"
                  ? "حجز مجاني"
                  : `${Number(lastResult.amount).toLocaleString("ar-SA")} ر.س`}
              </div>
              <div className="text-xs text-[#5E5C58] mt-0.5" dir="ltr">
                Booking ID: {lastResult.booking_id.slice(0, 8)}…
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={copyInviteLink}
                disabled={inviteLoading}
                className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="manual-copy-invite-btn"
              >
                {inviteLoading ? "جاري الإنشاء..." : "نسخ رابط الدعوة"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLastResult(null)}
                className="rounded-full"
                data-testid="manual-dismiss-result"
              >
                إخفاء
              </Button>
            </div>
          </div>
          <p className="mt-3 text-xs text-[#5E5C58] leading-relaxed">
            تم إرسال بريد تأكيد للعميل يحتوي على رابط الدخول التلقائي. يمكنك أيضًا نسخ الرابط هنا
            للإرسال عبر الواتساب أو Botim (صالح لمرة واحدة، خلال ٧ أيام).
          </p>

          {lastResult.generated_password && (
            <div
              data-testid="manual-generated-credentials"
              className="mt-3 rounded-xl bg-[#FFF8EC] border border-[#F4DDA8] p-3 text-xs text-[#5C4520] leading-relaxed"
            >
              <div className="font-semibold mb-1">بيانات الدخول المُنشأة للعميل:</div>
              <div className="flex items-center justify-between gap-2 font-mono" dir="ltr">
                <span>{lastResult.email}</span>
                <span className="font-bold">{lastResult.generated_password}</span>
              </div>
              <div className="mt-1 text-[11px] text-[#8A6A1F]">
                أُرسلت هذه البيانات تلقائيًا في بريد التأكيد. لن تظهر هنا مجددًا بعد إخفاء البطاقة.
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-warm p-6">
          <h3 className="font-heading text-xl text-[#222222]">بيانات العميل</h3>
        <p className="text-[#5E5C58] text-sm mt-1">
          سيُنشأ الحساب تلقائيًا في حال عدم وجوده مسبقًا، وتُرسل رسالة التأكيد للبريد المُدخل.
        </p>
        <div className="grid sm:grid-cols-3 gap-3 mt-4">
          <Input value={first} onChange={(e) => setFirst(e.target.value)}
                 placeholder="الاسم الأول" data-testid="manual-first-name" />
          <Input value={middle} onChange={(e) => setMiddle(e.target.value)}
                 placeholder="اسم الأب" data-testid="manual-middle-name" />
          <Input value={last} onChange={(e) => setLast(e.target.value)}
                 placeholder="اسم العائلة" data-testid="manual-last-name" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                 placeholder="email@example.com" dir="ltr" data-testid="manual-email" />
          <PhoneInput
            value={phone}
            onChange={setPhone}
            testId="manual-phone"
            placeholder="5XXXXXXXX"
          />
        </div>

        <h3 className="font-heading text-xl text-[#222222] mt-7">الخدمة والدفع</h3>
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <select
            value={serviceId}
            onChange={(e) => setServiceId(e.target.value)}
            className="border border-[#E5E1D8] rounded-xl px-3 py-2 bg-white"
            data-testid="manual-service-select"
          >
            {services.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title_ar} — {Number(s.price).toLocaleString("ar-SA")} ر.س
              </option>
            ))}
          </select>
          <select
            value={paymentStatus}
            onChange={(e) => setPaymentStatus(e.target.value)}
            className="border border-[#E5E1D8] rounded-xl px-3 py-2 bg-white"
            data-testid="manual-payment-select"
          >
            <option value="paid">مدفوع (تحويل بنكي / يدوي)</option>
            <option value="complimentary">مجاني / تعويض</option>
          </select>
        </div>
        <Input value={note} onChange={(e) => setNote(e.target.value)}
               placeholder="ملاحظة داخلية (اختياري)"
               className="mt-3"
               data-testid="manual-note" />

        <Button
          onClick={submit}
          disabled={submitting}
          className="mt-6 rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white"
          data-testid="manual-submit-btn"
        >
          {submitting ? "جاري الحفظ..." : "إنشاء الحجز"}
        </Button>
      </div>

      <div className="card-warm p-6">
        <h3 className="font-heading text-xl text-[#222222]">الموعد</h3>
        <p className="text-[#5E5C58] text-sm mt-1">
          {isImmediate
            ? "جلسة أوتاد فورية: يمكن الحجز في أي يوم من الأسبوع."
            : "يتم تطبيق نفس قواعد الحد الزمني الخاصة بالخدمة المحددة."}
        </p>
        <div className="mt-4 grid sm:grid-cols-2 gap-4 items-start">
          <div className="bg-[#F6F4EE] rounded-2xl p-2 inline-block" style={{ direction: "ltr" }}>
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={manualDisabled}
              weekStartsOn={0}
              showOutsideDays={true}
              components={calendarComponents}
              classNames={calendarRowClass}
              dir="ltr"
              data-testid="manual-calendar"
            />
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-[#5E5C58]">الساعة</label>
              <select
                value={hour}
                onChange={(e) => setHour(Number(e.target.value))}
                className="w-full mt-1 border border-[#E5E1D8] rounded-xl px-3 py-2 bg-white"
                data-testid="manual-hour-select"
              >
                {serviceHours.map((h) => (
                  <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
                ))}
              </select>
            </div>
            <p className="text-xs text-[#5E5C58]">
              سيتم تأكيد الحجز فورًا، مع إرسال بريد التأكيد، وحجب هذه الفترة من تقويم العملاء.
            </p>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export { ManualBookingForm };
